<template>
    <template v-if="isLoading">
        <loading />
    </template>
    <template v-else>
        <div class="pb-12">
            <h1 class="font-semibold text-3xl text-capDarkPurple">My Profile</h1>
        </div>
        <div class="flex flex-col lg:flex-row gap-6">
            <div class="flex-1 bg-white shadow rounded-lg">
                <div class="p-8">
                    <p class="text-capDarkPurple pb-4 text-xl font-semibold">Contact Information</p>

                    <div>
                        <p class="text-capDarkPurple">{{ profileInfo.fullName || '—' }}</p>
                        <p class="text-capDarkPurple pt-1">{{ profileInfo.zctWebPhone || '—' }}</p>
                        <p class="text-capDarkPurple pt-1 break-words" v-html="formattedEmail"></p>
                    </div>

                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        <div>
                            <p class="text-capGrey text-sm">Mailing Address</p>
                            <div v-if="profileInfo.zctWebAddressMailingLine1 != ''" class="text-capDarkPurple">
                                <p>{{ profileInfo.zctWebAddressMailingLine1 }}</p>
                                <p v-if="profileInfo.zctWebAddressMailingLine2">{{ profileInfo.zctWebAddressMailingLine2 }}</p>
                                <p>{{ profileInfo.zctWebAddressMailingCity }}, {{ profileInfo.zctWebAddressMailingState }} {{ profileInfo.zctWebAddressMailingZip }}</p>
                                <p>{{ profileInfo.zctWebAddressMailingCountry }}</p>
                            </div>
                            <p v-else class="text-capDarkPurple">—</p>
                        </div>

                        <div>
                            <p class="text-capGrey text-sm">Billing Address</p>
                            <div v-if="profileInfo.zctWebAddressBillingLine1 != ''" class="text-capDarkPurple">
                                <p>{{ profileInfo.zctWebAddressBillingLine1 }}</p>
                                <p v-if="profileInfo.zctWebAddressBillingLine2">{{ profileInfo.zctWebAddressBillingLine2 }}</p>
                                <p>{{ profileInfo.zctWebAddressBillingCity }}, {{ profileInfo.zctWebAddressBillingState }} {{ profileInfo.zctWebAddressBillingZip }}</p>
                                <p>{{ profileInfo.zctWebAddressBillingCountry }}</p>
                            </div>
                            <p v-else class="text-capDarkPurple">—</p>
                        </div>
                    </div>

                    <div class="border-t border-capGrey border-opacity-30 mt-8 pt-8">
                        <p class="text-capDarkPurple pb-4 text-xl font-semibold">Contact Methods</p>

                        <loading v-if="savingDefault" />
                        <template v-else>
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div v-if="emailMethods.length">
                                <p class="text-capGrey text-sm mb-3">Email</p>
                                <div v-for="method in emailMethods" :key="method.contact" class="flex items-center gap-3 py-1">
                                    <input
                                        type="checkbox"
                                        :id="'email-' + method.contact"
                                        :checked="defaultEmailContact === method.contact"
                                        @click.prevent="setDefaultEmail(method)"
                                        class="h-4 w-4 rounded border-gray-300 cursor-pointer text-capPurple focus:ring-capPurple"
                                    />
                                    <span class="text-capDarkPurple text-sm break-words">
                                        <span v-html="wrapEmail(method.contact)"></span>
                                        <span v-if="defaultEmailContact === method.contact" class="text-capGrey text-xs ml-1">(Default)</span>
                                    </span>
                                </div>
                            </div>

                            <div v-if="phoneMethods.length">
                                <p class="text-capGrey text-sm mb-3">Phone</p>
                                <div v-for="method in phoneMethods" :key="method.contact" class="flex items-center gap-3 py-1">
                                    <input
                                        type="checkbox"
                                        :id="'phone-' + method.contact"
                                        :checked="defaultPhoneContact === method.contact"
                                        @click.prevent="setDefaultPhone(method)"
                                        class="h-4 w-4 rounded border-gray-300 cursor-pointer text-capPurple focus:ring-capPurple"
                                    />
                                    <span class="text-capDarkPurple text-sm">
                                        {{ method.contact }}
                                        <span class="text-capGrey text-xs ml-1">({{ method.type }})</span>
                                        <span v-if="defaultPhoneContact === method.contact" class="text-capGrey text-xs ml-1">(Default)</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <p v-if="!emailMethods.length && !phoneMethods.length" class="text-capGrey text-sm">No contact methods on file.</p>

                        <button
                            v-if="activeContracts.length"
                            @click="openAddContactDialog"
                            class="mt-6 flex items-center gap-2 text-sm text-capPurple hover:text-capDarkPurple transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                            </svg>
                            Add Contact Method
                        </button>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Add Contact Method Dialog -->
            <Teleport to="body">
                <div
                    v-if="showAddContactDialog"
                    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                    @click.self="closeAddContactDialog"
                >
                    <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 overflow-hidden">
                        <div class="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
                            <h3 class="text-capDarkPurple font-semibold text-base">Add Contact Method</h3>
                            <button @click="closeAddContactDialog" class="text-capGrey hover:text-capDarkPurple transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div class="px-6 py-5 space-y-5">
                            <div>
                                <p class="text-capGrey text-xs mb-3">Type</p>
                                <div class="flex gap-6">
                                    <label class="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            v-model="newContact.type"
                                            value="Email"
                                            class="h-4 w-4 text-capPurple focus:ring-capPurple cursor-pointer"
                                        />
                                        <span class="text-capDarkPurple text-sm select-none">Email</span>
                                    </label>
                                    <label class="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            v-model="newContact.type"
                                            value="Phone"
                                            class="h-4 w-4 text-capPurple focus:ring-capPurple cursor-pointer"
                                        />
                                        <span class="text-capDarkPurple text-sm select-none">Phone</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label class="text-capGrey text-xs block mb-1">
                                    {{ newContact.type === 'Email' ? 'Email Address' : 'Phone Number' }}
                                </label>
                                <input
                                    v-if="newContact.type === 'Email'"
                                    type="email"
                                    v-model="newContact.value"
                                    placeholder="you@example.com"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-capDarkPurple placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                                />
                                <input
                                    v-else
                                    type="tel"
                                    v-model="newContact.value"
                                    placeholder="(555) 555-5555"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-capDarkPurple placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                                />
                            </div>

                            <label class="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    v-model="newContact.setAsDefault"
                                    class="h-4 w-4 rounded border-gray-300 text-capPurple focus:ring-capPurple cursor-pointer"
                                />
                                <span class="text-capDarkPurple text-sm select-none">Set as default</span>
                            </label>
                        </div>
                        <div class="flex justify-end gap-3 px-6 pb-5">
                            <button
                                @click="closeAddContactDialog"
                                class="px-4 py-2 text-sm text-capGrey hover:text-capDarkPurple transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                @click="onSaveNewContact"
                                :disabled="!newContact.value.trim() || addContactSaving"
                                class="px-4 py-2 text-sm bg-capPurple text-white rounded-md hover:bg-capDarkPurple transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {{ addContactSaving ? 'Saving…' : 'Save' }}
                            </button>
                        </div>
                    </div>
                </div>
            </Teleport>

            <div class="flex-1 bg-white shadow rounded-lg mb-8">
                <div class="p-8">
                    <p class="text-capDarkPurple pb-4 text-xl font-semibold">Protected Properties</p>
                    <loading v-if="loadingProtectedProperties" />
                    <template v-else>
                    <div v-if="activeContracts.length === 0" class="text-capGrey text-sm">
                        No active contracts found.
                    </div>
                    <div v-else class="space-y-4">
                        <div v-for="contract in activeContracts" :key="contract.__kptID"
                            class="border border-capGrey border-opacity-30 rounded p-4">
                            <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                <div>
                                    <c-badge :status="contract.status" class="mb-2">
                                        {{ contract.status }}
                                    </c-badge>
                                    <p class="text-capGrey text-xs">Contract #</p>
                                    <p class="text-capDarkPurple">{{ contract.contractNumberDisplay }}</p>
                                    <p class="text-capDarkPurple mt-2">{{ contract.zctWebAddrLine1 }}</p>
                                    <p class="text-capDarkPurple">{{ contract.physicalAddrCity }}, {{ contract.physicalAddrState }} {{ contract.physicalAddrPostalCode }}</p>
                                </div>
                                <div class="flex flex-col justify-between">
                                    <div>
                                        <p class="text-capGrey text-xs">Default Payment Method</p>
                                        <template v-if="defaultCardFor(contract.__kptID)">
                                            <p class="text-capDarkPurple">{{ defaultCardFor(contract.__kptID).type }} &mdash; {{ defaultCardFor(contract.__kptID).last4 }}</p>
                                            <p class="text-capDarkPurple">{{ defaultCardFor(contract.__kptID).exp }}</p>
                                        </template>
                                        <p v-else class="text-capDarkPurple">&mdash;</p>
                                    </div>
                                    <div class="w-max mt-3">
                                        <p
                                            class="text-capPurple font-light underline cursor-pointer"
                                            role="button"
                                            tabindex="0"
                                            @click="openPaymentDrawer(contract.__kptID)"
                                            @keydown.enter="openPaymentDrawer(contract.__kptID)"
                                            @keydown.space.prevent="openPaymentDrawer(contract.__kptID)"
                                        >
                                            {{ loadingPaymentDrawerContractId === contract.__kptID ? 'Loading…' : 'Update Payment Method' }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </template>
                </div>
            </div>

            <payment-methods-drawer v-model="showPaymentDrawer" :contract-id="selectedContractId" />

            <!-- Update Login Email Dialog -->
            <Teleport to="body">
                <div
                    v-if="showLoginEmailDialog"
                    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                >
                    <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 overflow-hidden">
                        <div class="px-6 pt-5 pb-4 border-b border-gray-100">
                            <h3 class="text-capDarkPurple font-semibold text-base">Do you want to update your login email, too?</h3>
                            <p class="text-capGrey text-sm mt-1">CAP will be notified to review and update.</p>
                        </div>
                        <div class="px-6 py-5">
                            <div class="flex gap-6">
                                <label class="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        v-model="useAsLoginChoice"
                                        :value="1"
                                        class="h-4 w-4 text-capPurple focus:ring-capPurple cursor-pointer"
                                    />
                                    <span class="text-capDarkPurple text-sm select-none">Yes</span>
                                </label>
                                <label class="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        v-model="useAsLoginChoice"
                                        :value="0"
                                        class="h-4 w-4 text-capPurple focus:ring-capPurple cursor-pointer"
                                    />
                                    <span class="text-capDarkPurple text-sm select-none">No</span>
                                </label>
                            </div>
                        </div>
                        <div class="flex justify-end gap-3 px-6 pb-5">
                            <button
                                @click="cancelLoginEmailDialog"
                                class="px-4 py-2 text-sm text-capGrey hover:text-capDarkPurple transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                @click="confirmLoginEmailDialog"
                                class="px-4 py-2 text-sm bg-capPurple text-white rounded-md hover:bg-capDarkPurple transition-colors"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            </Teleport>
        </div>
    </template>
</template>

<script>
    import SharedAPI from '@/api/SharedAPI'
    import { getFmError } from '@/api/fmError'
    import { mapState } from 'vuex'
    import { useToast } from 'vue-toastification'
    import PaymentMethodsDrawer from '@/modules/Contracts/PaymentMethodsDrawer.vue'

export default {
  components: { PaymentMethodsDrawer },

  setup() {
    return { toast: useToast() }
  },

  data() {
    return {
        isLoading: true,
        profileInfo: {},
        contactMethods: [],
        activeContracts: [],
        contractPayments: {},
        loadingProtectedProperties: true,
        defaultEmailContact: null,
        defaultPhoneContact: null,
        showAddContactDialog: false,
        addContactSaving: false,
        savingDefault: false,
        newContact: {
            type: 'Email',
            value: '',
            setAsDefault: false,
        },
        showPaymentDrawer: false,
        selectedContractId: '',
        loadingPaymentDrawerContractId: null,
        showLoginEmailDialog: false,
        useAsLoginChoice: 0,
        pendingAction: null,
        pendingEmailMethod: null,
        pendingEmailPrevious: null,
    };
  },

  mounted() {
    this.getProfileInfo()
    this.getContracts()
  },

  watch: {
    // The drawer can add/delete/set a default card; refresh this contract's
    // cached default so the list doesn't keep showing stale data after close.
    showPaymentDrawer(isOpen) {
        if (!isOpen && this.selectedContractId) {
            this.refreshContractPaymentDefault(this.selectedContractId)
        }
    },
  },

  computed: {
    ...mapState(['user']),

    emailMethods() {
        return this.contactMethods.filter(m => m.type && m.type.toLowerCase().includes('email'))
    },

    phoneMethods() {
        return this.contactMethods.filter(m => m.type && !m.type.toLowerCase().includes('email'))
    },

    formattedEmail() {
        return this.wrapEmail(this.profileInfo.zctPrimaryEmail)
    },
  },

  methods: {
    escapeHtml: function(str) {
        var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
        return str.replace(/[&<>"']/g, function(c) { return map[c] })
    },

    wrapEmail: function(email) {
        if (!email) return '—'
        return this.escapeHtml(email).replace(/@/g, '@<wbr>').replace(/\./g, '.<wbr>')
    },

    getProfileInfo: async function() {
        try {
            var params = [{ '__kptID': this.user.kptID }]
            var res = await SharedAPI.performFind(params, 'contact')
            this.profileInfo = res.response.data[0].fieldData

            this.contactMethods = []
            this.defaultEmailContact = null
            this.defaultPhoneContact = null

            var paramsContactMethod = [{ '_kftContactID': this.user.kptID }]
            var resContactMethod = await SharedAPI.performFind(paramsContactMethod, 'contact_method')
            resContactMethod.response.data.forEach(contactMethod => {
                this.contactMethods.push(contactMethod.fieldData)
            })

            var defaultEmail = this.contactMethods.find(m =>
                m.type && m.type.toLowerCase().includes('email') && m.primaryFlag == 1
            )
            var defaultPhone = this.contactMethods.find(m =>
                m.type && !m.type.toLowerCase().includes('email') && m.primaryFlag == 1
            )
            if (defaultEmail) this.defaultEmailContact = defaultEmail.contact
            if (defaultPhone) this.defaultPhoneContact = defaultPhone.contact
        }
        catch (e) {
            console.error(e)
        }
        this.isLoading = false
    },

    getContracts: async function() {
        this.loadingProtectedProperties = true
        try {
            var params = [
                { _kftContactID: this.user.kptID },
                { status: 'Deleted', omit: 'true' }
            ]
            var visibleStatuses = ['30 Day Wait', 'Active', 'Pending Closing', 'Past Due', 'Pending Payment', 'Pending Renewal']
            var res = await SharedAPI.performFind(params, 'contract')
            res.response.data.forEach(item => {
                if (visibleStatuses.includes(item.fieldData.status)) {
                    this.activeContracts.push(item.fieldData)
                }
            })
            await this.getContractPayments()
        }
        catch (e) {
            console.error(e)
        }
        finally {
            this.loadingProtectedProperties = false
        }
    },

    // Fetched sequentially: "set_globals" is a prerequest script that sets
    // FileMaker global fields scoped to one contract before contract_detail's
    // zctPaymentMethodArray is evaluated, so parallel calls could clobber
    // each other's globals mid-request.
    getContractPayments: async function() {
        this.contractPayments = {}
        for (const contract of this.activeContracts) {
            const contractID = contract.__kptID
            try {
                var params = [{ '__kptID': contractID }]
                var res = await SharedAPI.performFind(params, 'contract_detail', false, 'set_globals', contractID)
                var raw = res.response.data[0].fieldData.zctPaymentMethodArray
                if (!raw) continue

                var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
                var cards = Object.values(parsed).map((card) => ({
                    type: card['Card Brand'],
                    exp: card['Card Exp'],
                    last4: card['Card Last 4'],
                    isPrimary: card['isPrimary'],
                }))
                var defaultCard = cards.find(c => c.isPrimary === '1') || cards[0]
                if (defaultCard) this.contractPayments[contractID] = defaultCard
            }
            catch (e) {
                console.error('[getContractPayments] contract', contractID, e)
            }
        }
    },

    defaultCardFor: function(contractID) {
        return this.contractPayments[contractID] || null
    },

    refreshContractPaymentDefault: async function(contractID) {
        try {
            var params = [{ '__kptID': contractID }]
            var res = await SharedAPI.performFind(params, 'contract_detail', false, 'set_globals', contractID)
            var raw = res.response.data[0].fieldData.zctPaymentMethodArray
            if (!raw) {
                delete this.contractPayments[contractID]
                return
            }

            var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
            var cards = Object.values(parsed).map((card) => ({
                type: card['Card Brand'],
                exp: card['Card Exp'],
                last4: card['Card Last 4'],
                isPrimary: card['isPrimary'],
            }))
            var defaultCard = cards.find(c => c.isPrimary === '1') || cards[0]
            if (defaultCard) {
                this.contractPayments = { ...this.contractPayments, [contractID]: defaultCard }
            } else {
                delete this.contractPayments[contractID]
            }
        }
        catch (e) {
            console.error('[refreshContractPaymentDefault] contract', contractID, e)
        }
    },

    // PaymentMethodsDrawer reads its contract details from the Vuex "contract"
    // state rather than a prop, so that state must be refreshed for the
    // selected contract before the drawer opens.
    openPaymentDrawer: async function(contractID) {
        if (this.loadingPaymentDrawerContractId) return
        this.loadingPaymentDrawerContractId = contractID
        try {
            var params = [{ '__kptID': contractID }]
            var res = await SharedAPI.performFind(params, 'contract_detail', false, 'set_globals', contractID)
            this.$store.commit('setContractDetails', res.response.data[0].fieldData)
            this.selectedContractId = contractID
            this.showPaymentDrawer = true
        }
        catch (e) {
            console.error('[openPaymentDrawer] contract', contractID, e)
            this.toast.error('Failed to load payment methods for this contract.')
        }
        finally {
            this.loadingPaymentDrawerContractId = null
        }
    },

    setDefaultEmail: function(method) {
        if (this.defaultEmailContact === method.contact) return
        this.pendingAction = 'setDefault'
        this.pendingEmailMethod = method
        this.pendingEmailPrevious = this.defaultEmailContact
        this.useAsLoginChoice = 0
        this.defaultEmailContact = method.contact
        this.showLoginEmailDialog = true
    },

    confirmLoginEmailDialog: function() {
        var context = this.pendingAction
        var useAsLogin = this.useAsLoginChoice
        this.showLoginEmailDialog = false
        this.pendingAction = null

        if (context === 'createContact') {
            this.submitNewContact(useAsLogin)
            return
        }

        var method = this.pendingEmailMethod
        var previous = this.pendingEmailPrevious
        this.pendingEmailMethod = null
        this.pendingEmailPrevious = null
        this.saveDefault(method, () => { this.defaultEmailContact = previous }, useAsLogin)
    },

    cancelLoginEmailDialog: function() {
        if (this.pendingAction === 'setDefault') {
            this.defaultEmailContact = this.pendingEmailPrevious
        }
        this.showLoginEmailDialog = false
        this.pendingAction = null
        this.pendingEmailMethod = null
        this.pendingEmailPrevious = null
    },

    setDefaultPhone: function(method) {
        if (this.defaultPhoneContact === method.contact) return
        var previous = this.defaultPhoneContact
        this.defaultPhoneContact = method.contact
        this.saveDefault(method, () => { this.defaultPhoneContact = previous })
    },

    saveDefault: async function(method, revert, useAsLogin) {
        // Assumes contact_method's own primary key follows the same __kptID
        // convention as contract/contact; unconfirmed since FileMaker access
        // has been unavailable while diagnosing the create-contact-method bug.
        this.savingDefault = true
        try {
            var contactMethodID = method.__kptID
            if (!contactMethodID) {
                console.error('[saveDefault] contact method has no __kptID:', method)
                this.toast.error('Unable to update default contact method.')
                revert()
                return
            }

            var params = { contactMethodID: contactMethodID, isPrimary: '1', useAsLogin: useAsLogin ? '1' : '0' }
            var res = await SharedAPI.runScript('Web - Set Default Contact Method', JSON.stringify(params))
            console.log('[saveDefault] params sent:', params, 'full response:', JSON.parse(JSON.stringify(res)))

            var error = getFmError(res)
            if (error) {
                this.toast.error(error)
                revert()
                return
            }

            await this.getProfileInfo()
        }
        catch (e) {
            console.error(e)
            this.toast.error('Failed to update default contact method. Please try again.')
            revert()
        }
        finally {
            this.savingDefault = false
        }
    },

    openAddContactDialog: function() {
        this.newContact = { type: 'Email', value: '', setAsDefault: false }
        this.showAddContactDialog = true
    },

    closeAddContactDialog: function() {
        this.showAddContactDialog = false
    },

    onSaveNewContact: function() {
        if (this.newContact.type === 'Email' && this.newContact.setAsDefault) {
            this.pendingAction = 'createContact'
            this.useAsLoginChoice = 0
            this.showLoginEmailDialog = true
            return
        }
        this.submitNewContact(0)
    },

    submitNewContact: async function(useAsLogin) {
        var contact = this.newContact.value.trim()
        if (!contact || this.addContactSaving) return

        var contractID = this.activeContracts[0] && this.activeContracts[0].__kptID
        if (!contractID) {
            this.toast.error('No active contract found for this account.')
            return
        }

        this.addContactSaving = true
        try {
            var params = {
                type: this.newContact.type,
                contact: contact,
                contractID: contractID,
                isPrimary: this.newContact.setAsDefault ? '1' : '0',
                useAsLogin: useAsLogin ? '1' : '0',
            }
            var res = await SharedAPI.runScript('Web - Create Contact Method', JSON.stringify(params))
            console.log('[submitNewContact] params sent:', params, 'full response:', JSON.parse(JSON.stringify(res)))

            var error = getFmError(res)
            if (error) {
                this.toast.error(error)
                return
            }

            this.closeAddContactDialog()
            await this.getProfileInfo()
            this.toast.success('Contact method added.')
        }
        catch (e) {
            console.error(e)
            this.toast.error('Failed to add contact method. Please try again.')
        }
        finally {
            this.addContactSaving = false
        }
    },
  }
}
</script>
